import type { FormControlProps } from "@chakra-ui/react";
import { Select } from "@equidefi/ui/forms";
import type { TOption } from "@equidefi/ui/forms/Select";
import type { FieldPath, FieldValues } from "react-hook-form";
import { useController, useFormContext } from "react-hook-form";

export type SmartMultiSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
  label?: string;
  options: TOption[];
  placeholder?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  controlProps?: FormControlProps;
  /** react-select closes after every pick by default; false keeps it open until the user clicks away. */
  closeMenuOnSelect?: boolean;
  /** Adjusts the selection before it is stored, e.g. to make one option exclusive of the rest. */
  normalize?: (next: unknown[], current: unknown[]) => unknown[];
};

function SmartMultiSelect<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  label,
  options,
  placeholder = "Any",
  isRequired,
  isDisabled,
  controlProps = { mb: 2 },
  closeMenuOnSelect,
  normalize,
}: SmartMultiSelectProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>();
  const {
    field,
    fieldState: { error },
  } = useController<TFieldValues, TName>({ name, control });

  const selectedValues: unknown[] = field.value ?? [];
  const selected = options.filter((option) => selectedValues.includes(option.value));

  return (
    <Select
      isMulti
      name={field.name}
      label={label}
      options={options}
      value={selected}
      closeMenuOnSelect={closeMenuOnSelect}
      onChange={(next) => {
        const values = (next as readonly TOption[]).map((option) => option.value);
        field.onChange(normalize ? normalize(values, selectedValues) : values);
      }}
      isInvalid={!!error}
      error={error?.message}
      isRequired={isRequired}
      isDisabled={isDisabled}
      placeholder={placeholder}
      controlProps={controlProps}
    />
  );
}

export default SmartMultiSelect;
